import PropTypes from 'prop-types';
import {
    Button, DropdownSelectField, Link, ModalBody, ModalFooter, ModalHeader,
    ModalNext, useModal
} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import React, {
    useCallback, useEffect, useState
} from 'react';
import { useDataRefresh, ViewModelForm } from 'gw-portals-viewmodel-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import { useValidation } from 'gw-portals-validation-react';
import _ from 'lodash';
import { FileUtil } from 'gw-portals-util-js';
import { removeAccents } from 'gw-capability-document-react/util/DocumentUtil';
import metadata from './AddDocumentPopup_AX.metadata.json5';
import styles from './AddDocumentPopup_AX.module.scss';
import messages from './AddDocumentPopup_AX.messages';

function AddDocumentPopupAX(props) {
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate } = useValidation('AddDocumentPopupAX');
    const [documentsDTO, setDocumentsDTO] = useState({});
    const [isLoading, setLoadingState] = useState(false);
    const [docsArray, updateDocsArray] = useState([]);
    const [docTypeAvailableValues, setDocTypeAvailableValues] = useState([]);
    const {
        showAlert
    } = useModal();
    const documentTypes = [
        'AX_constitutivea_act', 'AX_death_certificate', 'AX_additional_information_letter',
        'AX_medical_expenses_letter', 'AX_tax_id', 'AX_InvoiceSupplement', 'AX_proof_address',
        'AX_clabe_account_document', 'AX_customer_invoice', 'AX_supplier_invoices',
        'AX_customer_identification_payment_format', 'AX_id_identification', 'AX_medical_report',
        'AX_research', 'AX_medical_pass', 'AX_third_medical_pass', 'AX_power_attorney',
        'AX_research_stands', 'AX_field_supervisions', 'AX_quotation', 'AX_Accident_Notice',
        'AX_settlement', 'AX_preliminary_information', 'AX_final_information',
        'AX_reimbursement_request_letter', 'AX_roc_sipac', 'AX_roc_traditional', 'AX_photographs',
        'AX_crystals_accident_declaration', 'AX_circulation_card', 'AX_driving_license',
        'AX_photographs_vin_insured', 'AX_photographs_vin_third', 'AX_inpart', 'AX_DeductibleReceipt'
    ];

    const {
        isOpen,
        onReject,
        onResolve,
        serviceRequestID,
        existingDocuments,
        viewModelService,
        authHeader
    } = props;

    useEffect(() => {
        setDocumentsDTO(
            viewModelService.create({
                serviceRequestNumber: serviceRequestID,
                documents: []
            }, 'cc', 'edge.capabilities.servicerequest.dto.AX_DocumentGroup')
        );
        const availableValues = [];
        viewModelService.productMetadata.get('cc')
            .types.getTypelist('DocumentType').codes.forEach((typecode) => {
                if (documentTypes.includes(typecode.code)) {
                    availableValues.push({
                        id: typecode.code,
                        displayName: translator({ id: typecode.name, defaultMessage: 'TBD' })
                    });
                }
            });
        setDocTypeAvailableValues(availableValues);
        // only execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleModelChanged = useCallback((newModel) => {
        setDocumentsDTO(newModel);
        refreshData();
    }, [refreshData]);

    const handleGenericErrorModal = useCallback((error) => {
        showAlert({
            title: {
                id: 'portal.views.dashboard-table.service-request.GenericTitleError_AX',
                defaultMessage: 'There was an unexpected error'
            },
            message: translator({
                id: 'portal.views.dashboard-table.service-request.GenericBodyError_AX',
                defaultMessage: 'The system wan into an unexpected error, please share this code to the system administrator if the problem persist: Code: {codeNumber} - {codeKey}'
            }, {
                codeNumber: error.errorCode, codeKey: error.baseError
            }),
            status: 'error',
            icon: 'mi-error-outline'
        }).then(_.noop);
    }, [translator]);

    const getDocumentTypeDefault = useCallback((fileName) => {
        let result = 'letter_received';
        const nameShort = removeAccents(fileName.split('.')[0]).toUpperCase();
        const nameClear = removeAccents(fileName).toUpperCase();
        if (nameClear.includes('FOTOGRAFIA') || nameClear.includes('FOTOGRAFIAS')) {
            result = 'AX_photographs';
        }
        for (let i = 0; i < docTypeAvailableValues.length; i += 1) {
            const documentType = docTypeAvailableValues[i];
            const textTypeCode = removeAccents(documentType.displayName).toUpperCase();
            if (textTypeCode.includes(nameShort)) {
                result = documentType.id;
                break;
            }
        }
        return result;
    }, [docTypeAvailableValues]);

    const handleUploadDocument = useCallback(async (files) => {
        setLoadingState(true);
        const documentsArray = _.get(documentsDTO, 'documents.value', []);
        const uploadToken = await ServiceRequestService.generateUploadToken(authHeader);
        const dataArray = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const file of files) {
            const documentMetadataTemplate = {
                serviceRequestId: serviceRequestID,
                name: FileUtil.checkFileName(file.name, [...existingDocuments, ...documentsArray]),
                mimeType: file.type,
                sessionID: uploadToken,
                documentType: getDocumentTypeDefault(file.name)
            };
            let data;
            try {
                // eslint-disable-next-line no-await-in-loop
                data = await ServiceRequestService
                    .uploadDocument(file, documentMetadataTemplate, authHeader);
                dataArray.push(data);
            } catch (e) {
                handleGenericErrorModal(e);
            }
        }
        const updatedArray = [...documentsArray, ...dataArray];
        _.set(documentsDTO, 'documents.value', updatedArray);
        updateDocsArray(updatedArray);
        refreshData();
        setLoadingState(false);
    }, [authHeader, documentsDTO, existingDocuments, getDocumentTypeDefault,
        handleGenericErrorModal, refreshData, serviceRequestID
    ]);

    const onDeleteDoc = useCallback((doc) => {
        setLoadingState(true);
        ServiceRequestService.deleteDocumentAX(serviceRequestID, doc.publicID, authHeader)
            .then((success) => {
                if (success) {
                    _.set(documentsDTO, 'documents.value',
                        _.get(documentsDTO, 'documents.value')
                            .filter((item) => item.publicID !== doc.publicID));
                    updateDocsArray(_.get(documentsDTO, 'documents.value'));
                    refreshData();
                } else {
                    handleGenericErrorModal({ errorCode: 'Remote', codeKey: 'CN' });
                }
            })
            .catch((error) => {
                handleGenericErrorModal(error);
            })
            .finally(() => {
                setLoadingState(false);
            });
    }, [authHeader, documentsDTO, handleGenericErrorModal, refreshData, serviceRequestID]);

    const getRemoveIcon = useCallback(
        (item) => {
            return (
                <Button
                    onClick={(e) => { e.preventDefault(); onDeleteDoc(item); }}
                    icon="mi-close"
                    className={styles.closeIcon}
                />
            );
        },
        [onDeleteDoc]
    );

    const onSave = useCallback(() => {
        setLoadingState(true);
        ServiceRequestService.updateDocumentMetadataAX(documentsDTO.value, authHeader)
            .then(() => {
                refreshData();
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
                showAlert({
                    title: error.errorCode,
                    message: error.appErrorCode,
                    status: 'error',
                    icon: 'mi-error-outline'
                });
            })
            .finally(() => {
                setLoadingState(false);
            });
        onResolve();
    }, [authHeader, documentsDTO.value, onResolve, refreshData]);

    const validateSave = useCallback(() => {
        const docs = _.get(documentsDTO, 'documents.value');
        const docsWithoutDocumentType = docs.filter((doc) => doc.documentType === 'letter_received');

        if (docsWithoutDocumentType.length > 0) {
            showAlert({
                title: translator(messages.selectDocumentTypeTitle),
                message: messages.selectDocumentType,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then(_.noop);
        } else {
            onSave();
            history.go(0);
        }
    }, [documentsDTO, onSave, translator]);

    const onCancel = useCallback(() => {
        docsArray.forEach((doc) => onDeleteDoc(doc));
        onReject();
    }, [docsArray, onDeleteDoc, onReject]);

    const onDocTypeChanged = useCallback((newValue, row) => {
        const newDocsArray = _.cloneDeep(docsArray);
        newDocsArray.filter((doc) => doc.publicID === row.publicID)[0].documentType = newValue;
        updateDocsArray(newDocsArray);
        _.set(documentsDTO, 'documents.value', newDocsArray);
        refreshData();
    }, [docsArray, documentsDTO, refreshData]);

    const getNameLink = useCallback((item) => {
        const downloadLink = item.ax_ECM_URL;
        return (
            <Link
                icon="mi-download"
                href={downloadLink}
                className={styles.documentNameLink}
                target="_blank"
            >
                {item.name}
            </Link>
        );
    }, []);

    const renderDocTypeCell = useCallback((row) => {
        return (
            <DropdownSelectField
                id="docTypeDropdown"
                searchable
                availableValues={docTypeAvailableValues}
                onValueChange={onDocTypeChanged}
                model={row}
                value={row.documentType}
            />
        );
    }, [docTypeAvailableValues, onDocTypeChanged]);

    const overrides = {
        loader: {
            loaded: !isLoading
        },
        documentsUploadId: {
            visible: !isLoading
        },
        documentTableGrid: {
            data: docsArray,
            visible: docsArray.length > 0 && !isLoading
        },
        deleteAction: {
            onClick: onDeleteDoc
        },
    };

    const resolvers = {
        resolveComponentMap: {
        },
        resolveCallbackMap: {
            onUploadDocument: handleUploadDocument,
            getNameLink: getNameLink,
            renderDocTypeCell: renderDocTypeCell,
            getRemoveIcon: getRemoveIcon
        }
    };
    return (
        <ModalNext
            isOpen={isOpen}
            className={styles.addDocumentPanel}
            onRequestClose={onCancel}
        >
            <ModalHeader
                title={translator(messages.addDocumentTitle)}
                subtitle={translator(messages.addDocumentSubtitle)}
                icon="publish"
            />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={documentsDTO}
                    onValidationChange={onValidate}
                    onModelChange={handleModelChanged}
                    componentMap={resolvers.resolveComponentMap}
                    callbackMap={resolvers.resolveCallbackMap}
                    overrideProps={overrides}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="tertiary"
                    onClick={onCancel}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button onClick={validateSave} disabled={!isComponentValid}>
                    {translator(messages.save)}
                </Button>
            </ModalFooter>

        </ModalNext>
    );
}

AddDocumentPopupAX.propTypes = {
    isOpen: PropTypes.bool,
    serviceRequestID: PropTypes.string,
    existingDocuments: PropTypes.array
};

AddDocumentPopupAX.defaultProps = {
    isOpen: false,
    serviceRequestID: undefined,
    existingDocuments: []
};

export default AddDocumentPopupAX;
